import React from 'react';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { PublicCertificate as PublicCertificateResource } from '@/types';
import { useTranslation } from 'react-i18next';

interface Props {
    certificate: PublicCertificateResource;
}

const PublicCertificateContent: React.FC<Props> = ({ certificate }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = React.useState(false);
    const pageTitle = `Certificado - ${certificate.owner.name}`;

    const shareUrl = React.useMemo(() => {
        if (typeof window === 'undefined') {
            return certificate.share_url;
        }

        try {
            return new URL(certificate.share_url, window.location.origin).toString();
        } catch (error) {
            return certificate.share_url;
        }
    }, [certificate.share_url]);

    const downloadUrl = React.useMemo(() => {
        if (typeof window === 'undefined') {
            return certificate.download_url;
        }

        try {
            return new URL(certificate.download_url, window.location.origin).toString();
        } catch (error) {
            return certificate.download_url;
        }
    }, [certificate.download_url]);

    const palette = {
        accentSoft: 'bg-theme-light text-theme-text',
        button: 'bg-theme-primary hover:bg-theme-primary-hover focus:ring-theme-primary',
        outline: 'border-theme-border text-theme-text hover:bg-theme-light focus:ring-theme-primary',
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) {
            return t('certificatesPublic.pendingDate');
        }

        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(new Date(dateString));
    };

    const handleDownload = () => {
        if (downloadUrl) {
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            setCopied(false);
        }
    };

    const handleLinkedIn = () => {
        if (certificate.linkedin_url) {
            window.open(certificate.linkedin_url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <>
            <PageTitle title={pageTitle} />
            <div className="relative min-h-screen bg-gradient-to-br from-neutral-100 via-white to-neutral-200 py-16 px-4 sm:px-6">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.04),_transparent_55%)]" aria-hidden />
                <div className="relative mx-auto flex max-w-5xl flex-col gap-10">
                    <div className="flex flex-col items-center gap-4 text-center">
                    {certificate.academy.logo_url && (
                        <div className="rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-black/5">
                            <img
                                src={certificate.academy.logo_url}
                                alt={certificate.academy.name}
                                className="h-14 w-auto"
                            />
                        </div>
                    )}
                    <p className={`text-xs font-medium uppercase tracking-[0.32em] text-gray-500 ${certificate.academy.logo_url ? '' : 'mt-2'}`}>
                        {certificate.academy.name}
                    </p>
                    <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
                        {certificate.evaluation.title}
                    </h1>
                    <div className="flex flex-col items-center gap-1 text-sm text-slate-600">
                        <span>{t('certificatesPublic.awardedTo', { name: certificate.owner.name })}</span>
                        <span>{t('certificatesPublic.issuedOn', { date: formatDate(certificate.issued_at) })}</span>
                    </div>
                </div>

                <div className="overflow-hidden rounded-3xl bg-white/90 shadow-2xl ring-1 ring-black/5 backdrop-blur">
                    <div className="grid gap-10 px-8 py-10 sm:px-12 md:grid-cols-[0.9fr,1.1fr]">
                        <div className="flex flex-col justify-between gap-8">
                            <div className="space-y-5">
                                <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${palette.accentSoft}`}>
                                    <span className="inline-flex h-2 w-2 rounded-full bg-current" />
                                    {t('certificatesPublic.signatureLabel', { id: certificate.signature_id.slice(0, 10).toUpperCase() })}
                                </div>
                                <div className="space-y-3">
                                    <h2 className="text-2xl font-semibold text-slate-900">
                                        {t('certificatesPublic.verifyHeadline')}
                                    </h2>
                                    <p className="text-sm leading-relaxed text-slate-600">
                                        {t('certificatesPublic.verifyDescription', {
                                            course: certificate.course.title,
                                            academy: certificate.academy.name,
                                        })}
                                    </p>
                                </div>
                                <dl className="grid gap-3 text-sm text-slate-600">
                                    <div className="flex items-center justify-between gap-4">
                                        <dt>{t('certificatesPublic.recipient')}</dt>
                                        <dd className="font-medium text-slate-900">{certificate.owner.name}</dd>
                                    </div>
                                    <div className="flex items-start justify-between gap-4">
                                        <dt>{t('certificatesPublic.course')}</dt>
                                        <dd className="text-right font-medium text-slate-900">{certificate.course.title}</dd>
                                    </div>
                                    <div className="flex items-center justify-between gap-4">
                                        <dt>{t('certificatesPublic.signatureId')}</dt>
                                        <dd className="font-mono text-xs text-slate-500">{certificate.signature_id}</dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleDownload}
                                    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-theme-on-primary shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${palette.button}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="h-4 w-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0L16.5 12m-4.5 4.5V3"
                                        />
                                    </svg>
                                    {t('certificatesPublic.downloadButton')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCopyLink}
                                    className={`inline-flex items-center justify-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${palette.outline}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="h-4 w-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M9 12h6m2.25 9H6.75A2.25 2.25 0 014.5 18.75V5.25A2.25 2.25 0 016.75 3h6.086c.597 0 1.169.237 1.591.659l4.914 4.914c.422.422.659.994.659 1.591v8.586A2.25 2.25 0 0120.25 21z"
                                        />
                                    </svg>
                                    {copied ? t('certificatesPublic.linkCopied') : t('certificatesPublic.copyLinkButton')}
                                </button>
                                {certificate.can_add_to_linkedin && certificate.linkedin_url && (
                                    <button
                                        type="button"
                                        onClick={handleLinkedIn}
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-transparent bg-[#0A66C2] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#094d96] focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:ring-offset-2"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 32 32"
                                            className="h-4 w-4"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="M27 3H5a2 2 0 00-2 2v22a2 2 0 002 2h22a2 2 0 002-2V5a2 2 0 00-2-2zM12 25H8V13h4zm-2-13a2.25 2.25 0 112.25-2.25A2.25 2.25 0 0110 12zm15 13h-4v-6.4c0-1.53-.62-2.6-2-2.6-1.06 0-1.64.71-1.91 1.39a2.2 2.2 0 00-.09.94V25h-4s.05-10 0-12h4v1.83a3.94 3.94 0 013.58-1.97c2.61 0 4.42 1.71 4.42 5.39z"
                                            />
                                        </svg>
                                        {t('certificatesPublic.addToLinkedIn')}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 shadow-inner">
                            {certificate.image_url ? (
                                <img
                                    src={certificate.image_url}
                                    alt={t('certificatesPublic.certificateImageAlt', { title: certificate.evaluation.title })}
                                    className="w-full"
                                />
                            ) : (
                                <div className="flex h-full min-h-[24rem] items-center justify-center text-sm text-slate-400">
                                    {t('certificatesPublic.certificateUnavailable')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

const PublicCertificate: React.FC<Props> = (props) => (
    <ThemeWrapper>
        <PublicCertificateContent {...props} />
    </ThemeWrapper>
);

export default PublicCertificate;
